/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2015, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * A service used to fetch and store units that the current account has recommended.
 *
 * NOTE: We no longer use the terminology "recommend" on the site. It has now been replaced by "likes". However,
 * we will continue using this service and other "recommend" terminology in the code.
 */
angular.module('bulbApp.recommendModule')
  .factory('BulbRecommendService', [
    '$',
    'Restangular',
    'BulbAuthService',
    'BulbErrorService',
    'SerialSaveService',
    '_',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    BulbRecommendService,
  ]);

function BulbRecommendService(
  $,
  Restangular,
  BulbAuthService,
  BulbErrorService,
  SerialSaveService,
  _,
) {
  return {
    /**
     * The already-retrieved recommended units.
     *  units: The retrieved units.
     *  previous: The id of the last retrieved unit.
     *  done: True when all available units have been retrieved.
     */
    recommends: {
      units: [],
      previous: null,
      done: false,
    },

    /**
   * Deletes the existing recommendation for the indicated unit.  This also removes the recommendation from the
   * appropriate recommends array.
   * @param unit Unit for which the recommendation should be deleted.
   * @returns {Promise} Promise that is resolved when the server responds from having deleted the recommendation.
   */
    deleteRecommend: function deleteRecommend(unit) {
      const that = this;
      return SerialSaveService.save({
        save: () => Restangular.one('units', unit.id)
          .customDELETE('likes')
          .then(() => {
            that.removeRecommend(unit.id);
          }),
      });
    },

    /**
     * Fetches a page of units.
     * @param size Max number of units to fetch.
     * @returns The promise returned by Restangular.
     */
    fetchRecommends: function fetchRecommends(size) {
      const that = this;
      if (!this.recommends.done) {
        const options = {
          size,
        };
        const { previous } = this.recommends;
        if (previous) {
          options.after = previous;
        }
        const accountId = BulbAuthService.currentUser.id;
        if (!accountId) {
          BulbErrorService.captureErrorMessage('Account Id is NULL while fetching recommends');
          return;
        }
        Restangular.one('accounts', accountId).customGET('likes', options).then((recommends) => {
          if (recommends.length > 0) {
            $.merge(that.recommends.units, recommends);
            // make sure there aren't any duplicates:
            that.recommends.units = _.uniq(that.recommends.units, 'id');
            that.recommends.previous = recommends[recommends.length - 1].id;
          }
          that.recommends.done = recommends.length < size;
        }, (error) => {
          BulbErrorService.error('Cannot fetch recommends', error);
        });
      }
    },

    /**
   * Add a recommended unit to the head of the list.
   * FUTURE: some more general way of updating interested components?
   * @param unit UnitDTO for the unit to be added.
   */
    addRecommend: function addRecommend(unit) {
      const { units } = this.recommends;
      units.unshift(unit);
    },

    /**
   * Remove a recommended unit from the list.
   * If it hasn't been fetched yet, assume it will already have been deleted when its page is finally fetched.
   * FUTURE: some more general way of updating interested components?
   * @param id id for the unit to be removed.
   */
    removeRecommend: function removeRecommend(id) {
      const { units } = this.recommends;
      for (let i = 0; i < units.length; i += 1) {
        if (units[i].id === id) {
          units.splice(i, 1);
          return;
        }
      }
    },

    /**
   * Create a new recommendation for the indicated unit and add it to the recommends array.
   * @param unit Unit for which a recommendation should be created.
   * @returns {Promise} Promise that is resolved when the server responds from having created the recommendation.
   */
    createRecommend: function createRecommend(unit) {
      const that = this;
      return SerialSaveService.save({
        save: () => Restangular.one('units', unit.id)
          .post('likes')
          .then(() => {
            that.addRecommend(unit);
          }),
      });
    },

    /**
   * If a recommended unit in the list has been updated, update it in the list too.
   * If it hasn't been fetched yet, assume it will already have been updated when it is finally fetched.
   * FUTURE: some more general way of updating interested components?
   * @param unit UnitDTO for the unit that has been updated.
   */
    updateRecommend: function updateRecommend(unit) {
      const { units } = this.recommends;
      for (let i = 0; i < units.length; i += 1) {
        if (units[i].id === unit.id) {
          units[i] = unit;
          return;
        }
      }
    },
  };
}
